/**
 * Exit Detection System
 * 
 * Detects when a player exits the page and triggers callbacks for handling.
 * 
 * CURRENT BEHAVIOR (as of 2026-02):
 * - Direct event sending is DISABLED
 * - Player Exited events are now ONLY sent by server-side websocket disconnect detection
 * - This system still detects exit events for logging/debugging purposes
 * - Tab visibility changes still trigger callbacks for separate visibility tracking
 * 
 * RATIONALE:
 * Browser-based exit detection is inherently unreliable (~40-60% success rate).
 * Server-side websocket disconnect detection provides nearly 100% reliability.
 * The client updates the server's exit payload via websocket, and the server
 * fires the Player Exited event when the websocket actually disconnects.
 * 
 * IMPORTANT: Tab unfocus fires a separate "Tab Visibility Changed" event.
 * Player Exit is reserved for actual page close/navigation events.
 */

import constants from '@playcastdotio/GlobalConfig';
import { captureBrowserData } from './browserDataCapture';

export type ExitReason = 'close' | 'navigation' | 'timeout' | 'error' | 'unknown';
export type VisibilityReason = 'unfocus' | 'focus';

export interface PlayerIdentifiers {
  persistentId: string;
  playerAuthId?: string;
  playerConnectionId?: string;
  broadcastSessionId?: string;
  inviteId?: string;
  flow?: 'quickstart';
  quickstartSlug?: string;
}

export interface ExitDetectionConfig {
  /**
   * Function to get current player identifiers
   */
  getPlayerIdentifiers: () => PlayerIdentifiers;
  
  /**
   * Optional: Function to get session start time (for sessionDuration calculation)
   */
  getSessionStartTime?: () => number | undefined;
  
  /**
   * Optional: Function to get page load time (for totalSessionDuration calculation)
   */
  getPageLoadTime?: () => number | undefined;
  
  /**
   * Optional: Callback to fire when exit is detected (for additional handling/logging)
   * This is called AFTER the fetch attempt
   */
  onExit?: (reason: ExitReason, metadata: ExitMetadata) => void;
  
  /**
   * Optional: Callback to fire when tab visibility changes (unfocus/focus)
   * This is separate from exit events and is called synchronously
   */
  onVisibilityChange?: (reason: VisibilityReason, metadata: ExitMetadata) => void;
}

export interface ExitMetadata {
  sessionDuration?: number;
  totalSessionDuration?: number;
}

/**
 * Instance-based exit detection state
 */
class ExitDetectionInstance {
  private exitEventFired = false;
  private cleanupFunctions: (() => void)[] = [];
  private config: ExitDetectionConfig;

  constructor(config: ExitDetectionConfig) {
    this.config = config;
  }

  /**
   * Fire exit event with duplicate prevention
   */
  private fireExitEvent(reason: ExitReason): void {
    // Prevent duplicate events
    if (this.exitEventFired) {
      console.log('[ExitDetection] Exit event already fired, skipping duplicate');
      return;
    }
    this.exitEventFired = true;

    // Get player identifiers
    const identifiers = this.config.getPlayerIdentifiers();
    
    // Calculate durations if available
    const sessionStartTime = this.config.getSessionStartTime?.();
    const pageLoadTime = this.config.getPageLoadTime?.();
    const now = Date.now();
    
    const sessionDuration = sessionStartTime ? now - sessionStartTime : undefined;
    const totalSessionDuration = pageLoadTime ? now - pageLoadTime : undefined;

    // Build metadata
    const metadata: ExitMetadata = {
      sessionDuration,
      totalSessionDuration,
    };

    // DISABLED: Direct send and fallback callback are disabled
    // We now rely solely on server-side websocket disconnect detection for Player Exited events
    // The sendExitPayloadToAPI() mechanism keeps the server payload updated
    
    // NOTE: Direct send disabled (Source 1 - exitDetection direct)
    // this.sendExitEvent(identifiers, reason, metadata);
    
    // NOTE: Fallback callback disabled (Source 2 - AnalyticsManager)
    // if (this.config.onExit) {
    //   this.config.onExit(reason, metadata);
    // }
    
    console.log('[ExitDetection] Exit detected, relying on server-side websocket disconnect for analytics');
  
  }

  /**
   * Reset exit event flag (when page becomes visible again)
   */
  resetExitFlag(): void {
    this.exitEventFired = false;
  }

  /**
   * Handle visibility change (tab unfocus/refocus)
   * Now fires a separate visibility event instead of exit event
   */
  handleVisibilityChange(): void {
    if (typeof document === 'undefined') {
      return;
    }

    // Calculate durations if available
    const sessionStartTime = this.config.getSessionStartTime?.();
    const pageLoadTime = this.config.getPageLoadTime?.();
    const now = Date.now();
    
    const sessionDuration = sessionStartTime ? now - sessionStartTime : undefined;
    const totalSessionDuration = pageLoadTime ? now - pageLoadTime : undefined;

    // Build metadata
    const metadata: ExitMetadata = {
      sessionDuration,
      totalSessionDuration,
    };

    if (document.visibilityState === 'hidden') {
      // Tab unfocused - fire visibility change event (not exit)
      console.log('[ExitDetection] Tab unfocused (not firing exit event)');
      if (this.config.onVisibilityChange) {
        this.config.onVisibilityChange('unfocus', metadata);
      }
    } else if (document.visibilityState === 'visible') {
      // Tab refocused
      console.log('[ExitDetection] Tab refocused');
      if (this.config.onVisibilityChange) {
        this.config.onVisibilityChange('focus', metadata);
      }
      // Reset exit flag when page becomes visible again
      this.resetExitFlag();
    }
  }

  /**
   * Handle page hide (tab close or navigation)
   */
  handlePageHide(event: PageTransitionEvent): void {
    // If persisted is false, the page is being unloaded (not just hidden)
    if (!event.persisted) {
      this.fireExitEvent('close');
    }
  }

  /**
   * Handle beforeunload (fallback for older browsers)
   */
  handleBeforeUnload(): void {
    this.fireExitEvent('close');
  }

  /**
   * Build AWS analytics payload format for Player Exit events
   * This matches the format expected by the analytics API
   */
  private buildExitEventPayload(
    identifiers: PlayerIdentifiers,
    reason: ExitReason,
    metadata: ExitMetadata
  ) {
    const browserData = captureBrowserData();
    const debugMode = !(constants.BRANCH === 'release');
    
    // Default tier filters
    const tier1Filters = ['all-analytics']; // Send to all providers

    return {
      eventName: 'Player Exited',
      eventParams: {
        name: 'Player Exited',
        role: 'player',
        persistentId: identifiers.persistentId,
        ...(identifiers.playerAuthId && { playerAuthId: identifiers.playerAuthId }),
        ...(identifiers.playerConnectionId && { playerConnectionId: identifiers.playerConnectionId }),
        ...(identifiers.broadcastSessionId && { broadcastSessionId: identifiers.broadcastSessionId }),
        ...(identifiers.inviteId && { inviteId: identifiers.inviteId }),
        reason,
        ...(metadata.sessionDuration !== undefined && { sessionDuration: metadata.sessionDuration }),
        ...(metadata.totalSessionDuration !== undefined && { totalSessionDuration: metadata.totalSessionDuration }),
        ...(identifiers.flow && { flow: identifiers.flow }),
        ...(identifiers.quickstartSlug && { quickstartSlug: identifiers.quickstartSlug }),
        delivery_method: 'exit_detection', // Indicates this uses sendBeacon or keepalive fetch
        delivery_reliability: 'low', // Flag that this event may not always arrive
        version: constants.VERSION,
        environment: constants.ENVIRONMENT,
        debug_mode: debugMode,
        tier1Filters: tier1Filters.join(','),
        // Browser context
        page_location: browserData._browser_context.page_location,
        page_title: browserData._browser_context.page_title,
        ...(browserData._browser_context.referrer && { page_referrer: browserData._browser_context.referrer }),
        ...(browserData._browser_context.utm_string && { utm_string: browserData._browser_context.utm_string }),
        screen_resolution: browserData._browser_context.screen_resolution,
        viewport_width: browserData._browser_context.viewport_width,
        viewport_height: browserData._browser_context.viewport_height,
        connection_type: browserData._browser_context.connection_type,
        ismobile: browserData._browser_context.ismobile,
      },
      ga4Fields: {
        ...(browserData.user_pseudo_id && { user_pseudo_id: browserData.user_pseudo_id }),
        device: browserData.device,
        privacy_info: browserData.privacy_info,
        traffic_source: browserData.traffic_source,
        ...(Object.keys(browserData.collected_traffic_source).length > 0 && {
          collected_traffic_source: browserData.collected_traffic_source,
        }),
        platform: browserData.platform,
        event_dimensions: browserData.event_dimensions,
      },
      ...(browserData.user_pseudo_id && { clientId: browserData.user_pseudo_id }),
      timestamp: Date.now(),
    };
  }

  /**
   * Build AWS analytics payload format for Tab Unfocused events
   * Similar to exit events but with different event name
   */
  private buildTabUnfocusedPayload(
    identifiers: PlayerIdentifiers,
    reason: VisibilityReason,
    metadata: ExitMetadata
  ) {
    const browserData = captureBrowserData();
    const debugMode = !(constants.BRANCH === 'release');
    
    // Default tier filters - same as other quickstart events
    const tier1Filters = ['all-analytics']; // Send to all providers
    const tier2Filters = identifiers.flow === 'quickstart' ? ['deaddrop'] : []; // Quickstart events go to deaddrop

    return {
      eventName: 'Tab Visibility Changed',
      eventParams: {
        name: 'Tab Visibility Changed',
        role: 'player',
        persistentId: identifiers.persistentId,
        ...(identifiers.playerAuthId && { playerAuthId: identifiers.playerAuthId }),
        ...(identifiers.playerConnectionId && { playerConnectionId: identifiers.playerConnectionId }),
        ...(identifiers.broadcastSessionId && { broadcastSessionId: identifiers.broadcastSessionId }),
        ...(identifiers.inviteId && { inviteId: identifiers.inviteId }),
        visibilityState: reason, // 'unfocus' or 'focus'
        ...(metadata.sessionDuration !== undefined && { sessionDuration: metadata.sessionDuration }),
        ...(metadata.totalSessionDuration !== undefined && { totalSessionDuration: metadata.totalSessionDuration }),
        ...(identifiers.flow && { flow: identifiers.flow }),
        ...(identifiers.quickstartSlug && { quickstartSlug: identifiers.quickstartSlug }),
        version: constants.VERSION,
        environment: constants.ENVIRONMENT,
        debug_mode: debugMode,
        tier1Filters: tier1Filters.join(','),
        tier2Filters: tier2Filters.join(','),
        // Browser context
        page_location: browserData._browser_context.page_location,
        page_title: browserData._browser_context.page_title,
      },
      ga4Fields: {
        ...(browserData.user_pseudo_id && { user_pseudo_id: browserData.user_pseudo_id }),
        device: browserData.device,
        platform: browserData.platform,
      },
      ...(browserData.user_pseudo_id && { clientId: browserData.user_pseudo_id }),
      timestamp: Date.now(),
    };
  }

  /**
   * Send exit event synchronously using best available method
   * Priority: sendBeacon (most reliable) > fetch+keepalive (fallback) > fetch (last resort)
   * 
   * CRITICAL: This method is 100% synchronous - no async/await
   * During page unload, async operations will be abandoned by the browser
   */
  private sendExitEvent(
    identifiers: PlayerIdentifiers,
    reason: ExitReason,
    metadata: ExitMetadata
  ): void {
    if (typeof window === 'undefined') {
      console.warn('[ExitDetection] Window not available');
      return;
    }

    try {
      // Build the payload matching the AWS analytics format
      const payload = this.buildExitEventPayload(identifiers, reason, metadata);
      const payloadString = JSON.stringify(payload);
      const url = getAnalyticsUrl();

      // Log payload for debugging (in development)
      if (constants.BRANCH !== 'release') {
        console.log('[ExitDetection] Sending exit event:', {
          eventName: payload.eventName,
          reason,
          persistentId: identifiers.persistentId,
          tier1Filters: payload.eventParams.tier1Filters,
          url,
          method: 'determining...'
        });
      }

      // Method 1: Try sendBeacon first (industry standard for exit events)
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        try {
          const blob = new Blob([payloadString], { type: 'application/json' });
          const sent = navigator.sendBeacon(url, blob);
          
          if (sent) {
            console.log(`[ExitDetection] Exit event sent via sendBeacon: ${reason}`, {
              persistentId: identifiers.persistentId,
              reason,
              sessionDuration: metadata.sessionDuration,
              totalSessionDuration: metadata.totalSessionDuration,
              method: 'sendBeacon',
            });
            return; // Success - exit early
          } else {
            console.warn('[ExitDetection] sendBeacon returned false (likely payload too large), falling back to fetch');
          }
        } catch (beaconError) {
          console.warn('[ExitDetection] sendBeacon failed, falling back to fetch:', beaconError);
        }
      }

      // Method 2: Fallback to fetch with keepalive (if probe completed and cached)
      if (typeof fetch === 'function') {
        // Use cached keepalive support (never await - must be synchronous)
        const useKeepalive = cachedKeepaliveSupport === true;
        
        const requestInit: RequestInit = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: payloadString,
        };

        // Add keepalive if we know it's supported (from pre-warmed cache)
        if (useKeepalive) {
          requestInit.keepalive = true;
        }

        // Fire and forget - don't await, browser will complete it
        // Note: .then() handlers may not execute during unload, but the request will be queued
        fetch(url, requestInit)
          .then((response) => {
            if (!response.ok) {
              console.warn(`[ExitDetection] Fetch failed with status ${response.status}: ${reason}`, {
                persistentId: identifiers.persistentId,
                useKeepalive,
                method: useKeepalive ? 'fetch+keepalive' : 'fetch',
              });
            } else {
              console.log(`[ExitDetection] Exit event sent via fetch: ${reason}`, {
                persistentId: identifiers.persistentId,
                reason,
                sessionDuration: metadata.sessionDuration,
                totalSessionDuration: metadata.totalSessionDuration,
                useKeepalive,
                method: useKeepalive ? 'fetch+keepalive' : 'fetch',
              });
            }
          })
          .catch((error) => {
            console.error('[ExitDetection] Fetch error:', error, {
              persistentId: identifiers.persistentId,
              reason,
              useKeepalive,
            });
          });
        
        return; // Request initiated
      }

      // Method 3: No viable send method available
      console.error('[ExitDetection] No viable method to send exit event (no sendBeacon or fetch)');
      
    } catch (error) {
      console.error('[ExitDetection] Error preparing exit event:', error, {
        url: getAnalyticsUrl(),
      });
    }
  }

  /**
   * Register event listeners
   */
  registerListeners(): void {
    // Create bound handlers
    const visibilityChangeHandler = () => this.handleVisibilityChange();
    const pageHideHandler = (event: PageTransitionEvent) => this.handlePageHide(event);
    const beforeUnloadHandler = () => this.handleBeforeUnload();

    // Register event listeners
    document.addEventListener('visibilitychange', visibilityChangeHandler);
    window.addEventListener('pagehide', pageHideHandler);
    window.addEventListener('beforeunload', beforeUnloadHandler);

    // Store cleanup functions
    this.cleanupFunctions.push(
      () => document.removeEventListener('visibilitychange', visibilityChangeHandler),
      () => window.removeEventListener('pagehide', pageHideHandler),
      () => window.removeEventListener('beforeunload', beforeUnloadHandler)
    );
  }

  /**
   * Cleanup all event listeners
   */
  cleanup(): void {
    this.cleanupFunctions.forEach(cleanup => cleanup());
    this.cleanupFunctions = [];
    this.exitEventFired = false;
  }
}

/**
 * Get the analytics API URL
 */
const getAnalyticsUrl = (): string => constants.ANALYTICS_API_URL;

// Keepalive probe configuration
const KEEPALIVE_PROBE_URL = 'https://www.google-analytics.com/g/collect';
const KEEPALIVE_PROBE_TIMEOUT_MS = 1000; // Reduced from 1500ms - we want this to complete quickly during init
let cachedKeepaliveSupport: boolean | null = null;
let keepaliveProbePromise: Promise<boolean> | null = null;

/**
 * Probe if keepalive fetch is supported
 * This is run during initialization (pre-warmed) to ensure the cache is ready
 * before any exit event fires. Never call this during page unload.
 */
async function runKeepaliveProbe(): Promise<boolean> {
  if (typeof window === 'undefined' || typeof fetch !== 'function') {
    return false;
  }

  // Test 1: Can we create a Request with keepalive?
  try {
    new Request(KEEPALIVE_PROBE_URL, {
      method: 'POST',
      body: 'probe',
      keepalive: true,
    });
  } catch {
    // Browser doesn't support keepalive at all
    return false;
  }

  // Test 2: Can we actually send a keepalive fetch?
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), KEEPALIVE_PROBE_TIMEOUT_MS);

  try {
    await fetch(KEEPALIVE_PROBE_URL, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      keepalive: true,
      signal: controller.signal,
    });
    return true;
  } catch (error) {
    // Keepalive not supported or network error
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Check if keepalive fetch is supported
 * Returns cached result if available (instant), otherwise starts probe
 * This should be pre-warmed during initialization
 */
async function supportsKeepaliveFetch(): Promise<boolean> {
  // Return cached result immediately if available
  if (cachedKeepaliveSupport !== null) {
    return cachedKeepaliveSupport;
  }

  // Start probe if not already running
  if (!keepaliveProbePromise) {
    keepaliveProbePromise = runKeepaliveProbe()
      .then((result) => {
        cachedKeepaliveSupport = result;
        return result;
      })
      .catch((error) => {
        console.warn('[ExitDetection] Keepalive probe error:', error);
        cachedKeepaliveSupport = false;
        return false;
      });
  }

  return keepaliveProbePromise;
}

/**
 * Pre-warm the keepalive probe to ensure result is cached before any exit event
 * This is called automatically during initialization, but can also be called
 * manually earlier in the page lifecycle for even better reliability.
 * 
 * Safe to call multiple times - will only run probe once.
 */
export function prewarmKeepaliveProbe(): void {
  if (typeof window === 'undefined') {
    return;
  }

  // Start the probe (don't await - let it run in background)
  supportsKeepaliveFetch()
    .then((supported) => {
      console.log('[ExitDetection] Keepalive probe completed:', {
        supported,
        cached: cachedKeepaliveSupport !== null,
      });
    })
    .catch((error) => {
      console.warn('[ExitDetection] Keepalive probe failed:', error);
    });
}

/**
 * Initialize exit detection system
 * Returns a cleanup function to remove event listeners
 */
export const initializeExitDetection = (config: ExitDetectionConfig): (() => void) => {
  if (typeof window === 'undefined') {
    console.warn('[ExitDetection] Window not available, skipping initialization');
    return () => {}; // Return no-op cleanup
  }

  // CRITICAL: Pre-warm the keepalive probe immediately
  // This ensures the cache is populated before any exit event fires
  // The probe runs async in background, but we don't wait for it
  prewarmKeepaliveProbe();

  // Create instance with instance-based state
  const instance = new ExitDetectionInstance(config);
  instance.registerListeners();

  console.log('[ExitDetection] Exit detection initialized (keepalive probe pre-warming)');

  // Return cleanup function
  return () => {
    instance.cleanup();
  };
};

